// Feature: docs/features/ionm-frontend-list-support.md
import React, { useMemo } from 'react';
import {
  Box,
  Heading,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Alert,
  AlertIcon,
  Link,
} from '@chakra-ui/react';
import { ExternalLinkIcon } from '@chakra-ui/icons';

const AdjustedClaimView = ({ results, cptDataSource, claimType, usedDocuments, handleSourceClick }) => {
  const tableData = useMemo(() => {
    const adjustedCodes = cptDataSource;
    const bestCaseCodes = results?.results?.icd10_best_case;
    const optimalCodes = results?.results?.optimal_icd10_codes?.recommended_codes;

    if (!adjustedCodes || !bestCaseCodes) {
      return [];
    }

    // Create a set of optimal ICD-10 codes for efficient lookup
    const optimalCodeSet = new Set(optimalCodes?.map(c => c.code) || []);

    // Normalize to an array of objects, handling both legacy dict and new list formats
    const normalizedCodes = Array.isArray(adjustedCodes) 
      ? adjustedCodes 
      : Object.entries(adjustedCodes).map(([key, details]) => ({ cpt_code: key, ...details }));

    const transformedData = normalizedCodes.map((cptDetails) => {
      const actualCptCode = cptDetails.cpt_code;
      const optimalJustificationsMap = new Map();
      const researchJustificationsMap = new Map();

      bestCaseCodes.forEach(bestCaseCode => {
        if (bestCaseCode.supporting_cpts && Array.isArray(bestCaseCode.supporting_cpts)) {
          bestCaseCode.supporting_cpts.forEach(supportingCpt => {
            if (supportingCpt.cpt_code != null &&
                String(supportingCpt.cpt_code) === String(actualCptCode) &&
                supportingCpt.source_article) {
              
              const isOptimal = optimalCodeSet.has(bestCaseCode.code);
              const targetMap = isOptimal ? optimalJustificationsMap : researchJustificationsMap;

              if (!targetMap.has(bestCaseCode.code)) {
                targetMap.set(bestCaseCode.code, new Set());
              }
              targetMap.get(bestCaseCode.code).add(supportingCpt.source_article);
            }
          });
        }
      });

      const formatJustifications = (justificationMap) => {
        return Array.from(justificationMap.entries()).map(([icdCode, articleSet]) => {
          const articles = Array.from(articleSet).join(', ');
          return { icdCode, articles };
        });
      };

      let modifiersList = '';
      if (Array.isArray(cptDetails.modifiers)) {
        modifiersList = cptDetails.modifiers.join(', ');
      } else if (typeof cptDetails.modifiers === 'string') {
        modifiersList = cptDetails.modifiers;
      } else if (cptDetails.modifier) {
        modifiersList = cptDetails.modifier;
      }

      return {
        cptCode: actualCptCode,
        modifier: modifiersList,
        units: cptDetails.units,
        optimalJustifications: formatJustifications(optimalJustificationsMap),
        researchJustifications: formatJustifications(researchJustificationsMap),
      };
    });

    return transformedData;
  }, [results, cptDataSource, claimType]);

  return (
    <Box>
      <Heading size="md" mb={4}>
        CMS-1500 Claim Line Summary
      </Heading>
      <Text fontSize="sm" color="gray.600" mb={4}>
        This view summarizes the optimal claim by linking each procedure (CPT) to the recommended diagnoses (ICD-10) and the payer policy articles that justify the pairing.
      </Text>
      <TableContainer borderWidth="1px" borderRadius="md">
        <Table variant="simple">
          <Thead>
            <Tr>
              <Th>CPT Code</Th>
              <Th>Modifier</Th>
              <Th>Units</Th>
              <Th>Justifying Diagnoses (Optimal)</Th>
              <Th>Needs Research (Best-Case)</Th>
            </Tr>
          </Thead>
          <Tbody>
            {tableData.map((row, index) => (
              <Tr key={index}>
                <Td fontFamily="mono" fontWeight="bold">{row.cptCode}</Td>
                <Td>{row.modifier}</Td>
                <Td>{row.units}</Td>
                <Td>
                  {row.optimalJustifications.length > 0 ? (
                    row.optimalJustifications.map((justification, jIndex) => (
                      <Box key={jIndex} mb={2}>
                        <Text as="span" fontWeight="semibold" color="green.600">
                          {justification.icdCode}
                        </Text>
                        <Text as="span" ml={1}>
                          ({justification.articles.split(', ').map((article, aIndex) => (
                            <span key={aIndex}>
                              {aIndex > 0 && ', '}
                              <Link
                                href={`https://www.cms.gov/medicare-coverage-database/view/article.aspx?articleId=${article.replace(/\D/g, '')}`}
                                isExternal
                                color="blue.600"
                              >
                                {article} <ExternalLinkIcon mx="2px" />
                              </Link>
                            </span>
                          ))}
                          )
                        </Text>
                      </Box>
                    ))
                  ) : (
                    <Text as="span" color="gray.500" fontStyle="italic">No optimal justification</Text>
                  )}
                </Td>
                <Td>
                  {row.researchJustifications.length > 0 ? (
                    row.researchJustifications.map((justification, jIndex) => (
                      <Box key={jIndex} mb={2}>
                        <Text as="span" fontWeight="semibold" color="orange.600">
                          {justification.icdCode}
                        </Text>
                        <Text as="span" ml={1}>
                          ({justification.articles.split(', ').map((article, aIndex) => (
                            <span key={aIndex}>
                              {aIndex > 0 && ', '}
                              <Link
                                href={`https://www.cms.gov/medicare-coverage-database/view/article.aspx?articleId=${article.replace(/\D/g, '')}`}
                                isExternal
                                color="blue.600"
                              >
                                {article} <ExternalLinkIcon mx="2px" />
                              </Link>
                            </span>
                          ))}
                          )
                        </Text>
                      </Box>
                    ))
                  ) : (
                    <Text as="span" color="gray.500" fontStyle="italic">None</Text>
                  )}
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </TableContainer>

      {/* Supporting Documentation Section */}
      {usedDocuments && usedDocuments.length > 0 && (
        <Box mt={6}>
          <Heading size="sm" mb={3}>
            Supporting Documentation Used in Analysis
          </Heading>
          <Box borderWidth="1px" borderRadius="md" p={4} bg="gray.50">
            {usedDocuments.map((doc, index) => (
              <Box key={index} mb={2}>
                <Text fontSize="sm">
                  <Text as="span" fontWeight="semibold">
                    {doc.type}:
                  </Text>{' '}
                  {doc.id ? (
                    <Link color="blue.600" cursor="pointer" onClick={() => handleSourceClick(doc)}>
                      {doc.title}
                    </Link>
                  ) : (
                    doc.title
                  )}
                </Text>
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default AdjustedClaimView;